import { KnowledgeHubAdminService } from "src/api/services/KnowledgeHubAdminService"
import { SEGMENT_TYPE_GROUP_STANDARD } from "src/components/SegmentPicker/constants"
import { ISegmentType } from "src/components/SegmentPicker/type"
import { reportError } from "src/lib/report"

interface ISegmentInfo {
    id: number
    type: ISegmentType
    descendants?: number[]
}

export async function getKnowledgeItemSegmentUrl(
    knowledgeItemId: string,
    propertyObjectSegmentId?: number,
    segmentsList?: ISegmentInfo[],
): Promise<string | null> {
    try {
        const itemId = parseInt(knowledgeItemId, 10)
        if (isNaN(itemId)) {
            reportError(
                `Invalid knowledge item ID: ${knowledgeItemId}`,
                new Error("Invalid ID"),
            )
            return null
        }

        const knowledgeItem =
            await KnowledgeHubAdminService.getV1AdminKnowledgebaseKnowledgeItems(
                {
                    knowledgeItemId: itemId,
                },
            )

        const segments = knowledgeItem.segments ?? []

        if (segments.length === 0) {
            return null
        }

        let targetSegmentId: number | null = null

        if (
            propertyObjectSegmentId !== undefined &&
            segmentsList !== undefined
        ) {
            targetSegmentId = findLowestLevelSegment(
                segments,
                propertyObjectSegmentId,
                segmentsList,
            )
        }

        if (targetSegmentId === null) {
            targetSegmentId = selectLowestLevelSegmentFromList(
                segments,
                segmentsList,
            )
        }

        if (targetSegmentId === null && segments.length > 0) {
            targetSegmentId = segments[0]
        }

        return targetSegmentId !== null
            ? `/chatbot/properties/${targetSegmentId}/${itemId}`
            : null
    } catch (error) {
        reportError("Failed to load knowledge item", error)
        return null
    }
}

function findLowestLevelSegment(
    knowledgeItemSegments: number[],
    propertyObjectSegmentId: number,
    segmentsList: ISegmentInfo[],
): number | null {
    const propertyObjectSegment = segmentsList.find(
        (s) => s.id === propertyObjectSegmentId,
    )

    if (propertyObjectSegment === undefined) {
        return null
    }

    const descendantsAndSelf = [
        propertyObjectSegmentId,
        ...(propertyObjectSegment.descendants ?? []),
    ]

    const eligibleSegments = knowledgeItemSegments.filter((segmentId) =>
        descendantsAndSelf.includes(segmentId),
    )

    if (eligibleSegments.length === 0) {
        return null
    }

    return selectLowestLevelSegmentFromList(eligibleSegments, segmentsList)
}

function selectLowestLevelSegmentFromList(
    segmentIds: number[],
    segmentsList?: ISegmentInfo[],
): number | null {
    if (segmentIds.length === 0) {
        return null
    }

    if (segmentsList === undefined || segmentsList.length === 0) {
        return segmentIds[0]
    }

    const segmentsWithType = segmentIds
        .map((id) => {
            const segment = segmentsList.find((s) => s.id === id)
            return segment !== undefined ? { id, type: segment.type } : null
        })
        .filter((s): s is { id: number; type: ISegmentType } => s !== null)

    if (segmentsWithType.length === 0) {
        return segmentIds[0]
    }

    const lowestLevelSegment = segmentsWithType.reduce((lowest, current) => {
        const lowestIndex = SEGMENT_TYPE_GROUP_STANDARD.indexOf(lowest.type)
        const currentIndex = SEGMENT_TYPE_GROUP_STANDARD.indexOf(current.type)

        return currentIndex > lowestIndex ? current : lowest
    })

    return lowestLevelSegment.id
}
